import React, { useCallback, useEffect, useRef, useState } from 'react';

import { JobsService, JobData } from '../../api/JobsService';
import { getTokenFromCookie } from '../../utils/tokenHelper';
import { EVENTS } from '@jobtarget/utils/consts';
import {
  pushVideoScreenerJobSelect,
  pushVideoScreenerSearchWithoutJob,
} from '../../utils/analytics';
import './PlatformsJobBar.css';
import { installPlatformIconGuard } from '../../utils/platformIconGuard';

// The change-job offcanvas slides closed over `transform 0.3s` (base-offcanvas). We wait a
// touch longer than that before remounting the bar, so the slide-out finishes smoothly
// instead of being cut off by the remount.
const OFFCANVAS_CLOSE_MS = 350;
// Safety net: reveal the bar even if the service-loaded event is somehow missed (on reload,
// when the element is already registered).
const READY_FALLBACK_MS = 5000;
// How long to wait for the <job-bar> custom element to register before deciding the platforms
// CDN failed to load (e.g. blocked by an ad/privacy extension) and falling back.
const DEFINE_TIMEOUT_MS = 6000;
/**
 * VI-773: applied to <change-job>'s slot-fallback nodes ("Select Job" header / "Cancel" footer)
 * that Stencil renders alongside our projected content. Hiding replaces the previous `remove()`,
 * which corrupted Stencil's vdom — see the effect that applies this. Rule in PlatformsJobBar.css.
 */
const SLOT_FALLBACK_HIDDEN_CLASS = 'jt-change-job-slot-fallback';

const JobBarSkeleton: React.FC = () => (
  <div className="platforms-job-bar__skeleton" aria-hidden="true">
    <div className="platforms-job-bar__skeleton-line platforms-job-bar__skeleton-line--title" />
    <div className="platforms-job-bar__skeleton-line platforms-job-bar__skeleton-line--action" />
  </div>
);

/**
 * VI-664: React wrapper around the platforms <job-bar> web component
 * (@jobtarget/shared-components, loaded via CDN — see index.html).
 *
 * It bridges the bar's window CustomEvents to the existing interview-page state:
 *  - jobBarJobChanged  -> hydrate the full JobData via JobsService.getJobById and
 *                         feed it through the same setSelectedJob callback the custom
 *                         selector used, so all downstream interview/filter logic is
 *                         preserved unchanged.
 *  - jobBarNoJobSelected -> clear the job and restore company-wide interviews.
 *
 * Rendered only when VITE_PLATFORMS_JOBBAR_ENABLED === 'true' (gated by the caller).
 */
interface PlatformsJobBarProps {
  selectedJob: JobData | null;
  setSelectedJob: (job: JobData | null) => void;
  onRestoreCompanyInterviews?: () => void;
  // Called if the <job-bar> custom element never registers (CDN blocked/failed), so the
  // caller can fall back to the legacy job selector instead of showing broken markup.
  onUnavailable?: () => void;
}

const PlatformsJobBar: React.FC<PlatformsJobBarProps> = ({
  selectedJob,
  setSelectedJob,
  onRestoreCompanyInterviews,
  onUnavailable,
}) => {
  const authToken = getTokenFromCookie();
  const env = import.meta.env.VITE_PLATFORMS_JOBBAR_ENV;

  // Show a skeleton while the bar (re)loads — both on initial CDN mount AND on every job
  // change (the `key` below remounts the bar, which spins up a fresh service). The bar's own
  // internal skeleton is suppressed by `allow-no-job`, so we provide our own. Reveal is driven
  // by the `jobBarServiceLoaded` event; a fallback timer guarantees we never get stuck.
  const [isReady, setIsReady] = useState(false);
  const revealTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const beginReload = useCallback(() => {
    setIsReady(false);
    if (revealTimerRef.current) clearTimeout(revealTimerRef.current);
    revealTimerRef.current = setTimeout(() => setIsReady(true), READY_FALLBACK_MS);
  }, []);

  // Keep the latest callbacks/state in refs so the window listeners (attached once)
  // never read stale closures.
  const setSelectedJobRef = useRef(setSelectedJob);
  const onRestoreRef = useRef(onRestoreCompanyInterviews);
  const onUnavailableRef = useRef(onUnavailable);
  const selectedJobRef = useRef(selectedJob);
  useEffect(() => {
    setSelectedJobRef.current = setSelectedJob;
    onRestoreRef.current = onRestoreCompanyInterviews;
    onUnavailableRef.current = onUnavailable;
    selectedJobRef.current = selectedJob;
  }, [setSelectedJob, onRestoreCompanyInterviews, onUnavailable, selectedJob]);

  useEffect(() => {
    // The <change-job> "Select" button emits `changeJobComplete`
    // (detail: { jobId, currentJob }) — this is the authoritative selection event,
    // same as candidate-manager wires. (`jobBarJobChanged` is not emitted by the
    // change-job modal flow.)
    const handleJobChanged = async (e: Event) => {
      const detail = (e as CustomEvent<{ jobId?: string; currentJob?: { jobId?: string } }>)
        .detail;
      const rawId = detail?.jobId ?? detail?.currentJob?.jobId;
      if (!rawId) return;

      const numericId = typeof rawId === 'string' ? parseInt(rawId, 10) : Number(rawId);
      if (Number.isNaN(numericId)) return;

      // Avoid a redundant refetch if the bar re-announces the already-selected job.
      if (selectedJobRef.current?.id === numericId) return;

      try {
        // Fetch in parallel with a short delay so the change-job offcanvas can finish
        // sliding closed before the bar remounts (the remount otherwise cuts the slide off).
        const [job] = await Promise.all([
          JobsService.getJobById(numericId),
          new Promise(resolve => setTimeout(resolve, OFFCANVAS_CLOSE_MS)),
        ]);
        // Track the selection under the Video Screener app (supplements the component's
        // hardcoded jt_candidate_manager_* event).
        const location = job.locations?.[0];
        pushVideoScreenerJobSelect({
          job_id: job.id,
          req_id: job.requisitionName,
          job_title: job.title,
          job_location: location
            ? [location.city, location.region, location.country].filter(Boolean).join(', ')
            : '',
          job_status: job.status,
        });
        // Show the skeleton while the new bar remounts/reloads, then commit the selection.
        beginReload();
        setSelectedJobRef.current(job);
      } catch (error) {
        console.error('[PlatformsJobBar] Failed to hydrate job', numericId, error);
      }
    };

    const handleNoJobSelected = () => {
      // Only act on a genuine clear (job -> no job). The <job-bar> also emits this
      // on mount when it loads empty (job-id=null); acting on that would clobber a
      // selection arriving at the same time (deep-link / Jobs-tab completed-screener
      // count click), leaving "No Job Selected" with job-scoped data. When nothing is
      // selected there is also nothing to restore — company data is the default.
      if (!selectedJobRef.current) return;
      beginReload();
      setSelectedJobRef.current(null);
      onRestoreRef.current?.();
    };

    // The bar's service dispatches this when it finishes (re)loading — reveal the bar.
    const handleServiceLoaded = () => {
      if (revealTimerRef.current) clearTimeout(revealTimerRef.current);
      setIsReady(true);
    };

    window.addEventListener(EVENTS.CHANGE_JOB.COMPLETE_EVENT, handleJobChanged as EventListener);
    window.addEventListener(EVENTS.JOB_BAR.NO_JOB_SELECTED_EVENT, handleNoJobSelected as EventListener);
    window.addEventListener(EVENTS.JOB_BAR_SERVICE.LOADED_EVENT, handleServiceLoaded as EventListener);
    // Initial-load probe: if the <job-bar> element has registered by now, reveal it (the
    // service-loaded event may just have been missed); if it never registered, the CDN was
    // blocked/failed — tell the caller to fall back to the legacy selector.
    revealTimerRef.current = setTimeout(() => {
      if (typeof customElements !== 'undefined' && customElements.get('job-bar')) {
        setIsReady(true);
      } else {
        onUnavailableRef.current?.();
      }
    }, DEFINE_TIMEOUT_MS);

    return () => {
      window.removeEventListener(
        EVENTS.CHANGE_JOB.COMPLETE_EVENT,
        handleJobChanged as EventListener
      );
      window.removeEventListener(
        EVENTS.JOB_BAR.NO_JOB_SELECTED_EVENT,
        handleNoJobSelected as EventListener
      );
      window.removeEventListener(
        EVENTS.JOB_BAR_SERVICE.LOADED_EVENT,
        handleServiceLoaded as EventListener
      );
      if (revealTimerRef.current) clearTimeout(revealTimerRef.current);
    };
    // Attach once; latest props are read through refs above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * VI-725: contain the v2 theme's runaway icon duplication in the platforms components
   * (job bar strip + the View Job panel's Video Screener tab). See platformIconGuard.ts for the
   * root cause and the upstream status (PA-1976). Document-scoped because the View Job panel
   * renders outside this component's subtree.
   */
  useEffect(() => installPlatformIconGuard(), []);

  /**
   * VI-725: hide the change-job offcanvas' leftover default header/footer.
   *
   * <change-job> declares its own fallback content inside the slots we fill:
   *   <slot name="change-job-header" …><h3>Select Job</h3></slot>
   *   <slot name="change-job-footer" …><base-button>Cancel</base-button></slot>
   * Because the component is `shadow: false`, Stencil's scoped-slot emulation renders that
   * fallback ALONGSIDE our projected content instead of replacing it — so the panel shows both
   * "Video Screener" and "Select Job" in the header, and both "Search Without A Job" and
   * "Cancel" in the footer.
   *
   * Only the fallback is targeted: our own slotted nodes are tagged `data-jt-slot`.
   *
   * VI-773 fixes two faults in the original implementation:
   *  1. It called `el.remove()`. Those nodes belong to Stencil, which still tracked them in its
   *     vdom — so its next patch called replaceChild/insertBefore against a parent that no longer
   *     held them, throwing "NotFoundError: Failed to execute 'replaceChild' on 'Node'" (QA saw
   *     189–200 of them after cycling the picker) and leaving the bar rendered as an empty shell.
   *     We now just add a hiding class. That is also an attribute mutation, not a childList one,
   *     so — unlike the removals — it cannot re-trigger the observer below.
   *  2. It searched only `#change-job-offcanvas`, which does not exist in the component build we
   *     load, so the sweep silently no-opped and BOTH fallbacks stayed visible ("Select Job"
   *     pinned in the header bar, a second "Cancel" in the footer). We now scope to the
   *     <change-job> element(s) themselves, keeping that id as an additional root for builds that
   *     do render it. The separate confirm-change-job modal lives outside <change-job>, so its
   *     legitimate Cancel button is still never touched.
   */
  useEffect(() => {
    const FALLBACK_TEXT = ['Select Job', 'Cancel'];
    let queued = false;

    const hideFallback = () => {
      queued = false;

      const roots = new Set<HTMLElement>();
      document
        .querySelectorAll<HTMLElement>('change-job, #change-job-offcanvas')
        .forEach((el) => roots.add(el));
      // The offcanvas may be rendered outside <change-job> (portalled to the panel host), so also
      // derive the root from OUR marker: the fallback shares a panel with our projected content.
      document.querySelectorAll<HTMLElement>('[data-jt-slot]').forEach((mine) => {
        const panel = mine.closest<HTMLElement>(
          'base-offcanvas, [class*="offcanvas"], [id*="change-job"], change-job'
        );
        if (panel) roots.add(panel);
      });

      roots.forEach((root) => {
        root.querySelectorAll<HTMLElement>('h3, base-button, button').forEach((el) => {
          if (!FALLBACK_TEXT.includes((el.textContent || '').trim())) return;
          if (el.closest('[data-jt-slot]')) return; // ours — keep
          // The confirm-change-job dialog has its OWN legitimate Cancel button. The roots above
          // are derived partly from our slot marker, so a nested dialog could fall inside one —
          // never touch anything that lives in a dialog/modal.
          if (el.closest('[role="dialog"], .modal, [id*="confirm" i], [class*="confirm" i]')) return;
          el.classList.add(SLOT_FALLBACK_HIDDEN_CLASS);
        });
      });
    };

    const observer = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(hideFallback);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    hideFallback();

    return () => observer.disconnect();
  }, []);

  // "Search Without A Job" footer: clear the selected job and restore the company-wide
  // video screeners. Clearing selectedJob flips the `key` below, remounting the bar and
  // tearing down the open change-job offcanvas (i.e. closing it).
  const handleSearchWithoutAJob = () => {
    pushVideoScreenerSearchWithoutJob();
    beginReload();
    setSelectedJobRef.current(null);
    onRestoreRef.current?.();
  };

  // Without an auth token the bar cannot fetch jobs; render nothing rather than an
  // unauthenticated bar (the gate's flag-off path keeps the custom selector available).
  if (!authToken) {
    return null;
  }

  return (
    <div className="platforms-job-bar" style={{ marginBottom: '1rem' }}>
      {/* Skeleton placeholder shown until the web component mounts. */}
      {!isReady && <JobBarSkeleton />}

      {/* The <job-bar> stays in the DOM so it can mount (and fire jobBarMounted) even while
          hidden; we just reveal it once ready to avoid a flash of the empty custom element.
          key forces a remount when the job changes externally (URL deep-link /
          interview-card click) so the bar reflects the new job-id. */}
      <div style={{ display: isReady ? 'block' : 'none' }}>
      <job-bar
        key={`platforms-job-bar-${selectedJob?.id ?? 'none'}`}
        job-id={selectedJob?.id ?? null}
        auth-token={authToken}
        env={env}
        allow-no-job
        show-change-job
        show-interviewer="true"
        change-job-id="interview-change-job"
        change-job-text="Change or Clear Job"
      >
        <div className="text-lg" slot="heading-text">
          Job Selected
        </div>
        {/* form-type="simple" lays the search fields out in a single row
            (keywords / division / job status), matching candidate-manager. */}
        <change-job
          id="interview-change-job"
          slot="change-job"
          job-id={selectedJob?.id ?? null}
          auth-token={authToken}
          env={env}
          form-type="simple"
        >
          {/* data-jt-slot marks OUR projected content so the fallback-stripping effect above
              never removes it (see the VI-725 comment on that effect). */}
          <div slot="change-job-header" data-jt-slot="header">
            <h3>Video Screener</h3>
          </div>
          <button
            type="button"
            className="btn btn-link mr-auto"
            slot="change-job-footer"
            data-jt-slot="footer"
            onClick={handleSearchWithoutAJob}
          >
            Search Without A Job
          </button>
        </change-job>
      </job-bar>
      </div>
    </div>
  );
};

export default PlatformsJobBar;
